import type { StateTree } from '@makemydeal/dr-dash-types';

import { createValidationEngine } from '@makemydeal/dr-shared-ui-utils';
import { accessoryValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/accessoryValidationConfig';
import { incentiveValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/incentiveValidationConfig';
import { outsideLienholderValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/tradeInValidationConfig';
import { vehicleProtectionProductValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/vehicleProtectionProductsValidationConfig';
import { getCurrentOfferType } from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { getAccessoriesList } from '@makemydeal/dr-dash-store/selectors/accessoriesSelectors';
import { getAppliedIncentives } from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { getNormalizedTradeInsForValidation } from '@makemydeal/dr-dash-store/selectors/tradeIns';
import { getVppProducts } from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import { flattenLeaf, flattenNestedErrorFlags } from '@makemydeal/dr-dash-store/utils/contractingUtils';
import { setOrClearErrors } from '@makemydeal/dr-dash-store/actions/saveValidationActionCreators';
import { SlideOutType } from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import { DealTabs } from '@makemydeal/dr-shared-types';

const SLIDEOUT_VALIDATION: Record<
    string,
    {
        entity: DealTabs;
        config: Record<string, any>;
        getData: (state: StateTree) => Record<string, any>;
        errorKey?: string;
    }
> = {
    [SlideOutType.Accessories]: {
        entity: DealTabs.ACCESSORIES,
        config: { accessories: { $collection: accessoryValidationConfig } },
        getData: (state) => {
            const list = getAccessoriesList(state);
            return { accessories: list?.length ? list : undefined };
        }
    },
    [SlideOutType.Incentives]: {
        entity: DealTabs.INCENTIVES,
        config: { incentives: { $collection: incentiveValidationConfig } },
        getData: (state) => {
            const list = getAppliedIncentives(state);
            return { incentives: list?.length ? list : undefined };
        }
    },
    [SlideOutType.TradeIn]: {
        entity: DealTabs.TRADE_IN,
        config: { tradeIns: { $collection: outsideLienholderValidationConfig } },
        getData: (state) => {
            const tradeIns = getNormalizedTradeInsForValidation(state);
            return { tradeIns: tradeIns.length > 0 ? tradeIns : undefined };
        },
        errorKey: 'tradeIns'
    },
    [SlideOutType.Protection]: {
        entity: DealTabs.PROTECTION,
        config: { vehicleProtectionProducts: { $collection: vehicleProtectionProductValidationConfig } },
        getData: (state) => {
            const list = getVppProducts(state);
            return { vehicleProtectionProducts: list?.length ? list : undefined };
        },
        errorKey: 'vehicleProtectionProducts'
    }
};

const SLIDEOUT_ERROR_ENTITY: Record<string, DealTabs> = {
    [SlideOutType.Accessories]: DealTabs.ACCESSORIES,
    [SlideOutType.Incentives]: DealTabs.INCENTIVES,
    [SlideOutType.TradeIn]: DealTabs.TRADE_IN,
    [SlideOutType.Protection]: DealTabs.PROTECTION
};

/**
 * Runs scoped validation for a single slideout entity against the current state,
 * then returns an action (setErrorFields or clearEntityErrors) to dispatch.
 * Returns undefined if the slideout has no validation mapping.
 */
export const validateSlideOutEntity = (currentSlideOut: string, state: StateTree) => {
    const entity = SLIDEOUT_ERROR_ENTITY[currentSlideOut];
    const validation = SLIDEOUT_VALIDATION[currentSlideOut];
    if (!entity || !validation) return undefined;

    const engine = createValidationEngine({
        initialConfig: validation.config,
        flow: 'default+save',
        lockFlow: true
    });
    engine.ctx.values = validation.getData(state);
    engine.ctx.offerType = getCurrentOfferType(state);
    engine.ctx.featureToggles = featureToggleSelectors.getTogglesWithoutSafetyChecks(state) as Record<string, boolean>;
    engine.validateAll();
    const errors = engine.getFormErrorState({ onlyActive: true });
    let errorFlags = flattenLeaf(errors, 'hasError');
    if (currentSlideOut === SlideOutType.TradeIn) {
        errorFlags = { tradeIns: flattenNestedErrorFlags(errorFlags['tradeIns']) };
    }
    return setOrClearErrors(errorFlags, validation.entity, validation.errorKey);
};
